import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import { useGetAccountOpportunities, useGetOffer, useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  useEgsDraftsContext,
  useInclusionDraftsContext,
  usePackageOptionsContext,
  useScrapeSessionContext,
  useSurchargeGridsContext,
} from '../PricingToolSessionContext';
import { egsRoomNames } from '../tabs/egs/egsModel';
import { chosenEgsRooms, egsRoomRows, reducePreviousEgs, withChosenEgsRooms } from '../tabs/egs/previousEgs';
import { deriveOpportunityCurrency } from '../tabs/inclusions/inclusionsModel';
import { reducePreviousInclusions } from '../tabs/inclusions/previousInclusions';
import { DEFAULT_OPTION_STATE } from '../tabs/packageOptions/packageOptionsModel';
import {
  previousLengthsOfStay,
  reducePreviousDealOptions,
  replaceWithPreviousRoomPricing,
} from '../tabs/packageOptions/previousDealOptions';
import { deriveOpportunityPrefill } from '../tabs/propertySetup/propertySetupModel';
import {
  type SurchargeDates,
  chosenSurchargeDates,
  countOverlappingNotAccumulating,
  isSurchargeDatesDone,
  reducePreviousSurcharges,
  withChosenSurchargeDates,
} from '../tabs/surcharges/previousSurcharges';

import CopyEgsRoomsStep from './CopyEgsRoomsStep';
import CopySectionOption from './CopySectionOption';
import CopySurchargeDatesStep from './CopySurchargeDatesStep';
import {
  COPY_SECTIONS,
  COPY_STEP_LABELS,
  CopySection,
  CopySectionDetails,
  CopyStep,
  canCopy,
  copySteps,
  dealOptionsNotes,
  dealOptionsSummary,
  egsNotes,
  egsSummary,
  inclusionsNotes,
  inclusionsSummary,
  isStepComplete,
  previousOpportunityChoices,
  replaceWarning,
  surchargesNotes,
  surchargesOverlapWarning,
  surchargesSummary,
  toggleCopySection,
} from './copyFromPreviousModel';

type CopyFromPreviousOpportunityDialogProps = {
  offerId: string;
  onClose: () => void;
  open: boolean;
};

export default function CopyFromPreviousOpportunityDialog({
  offerId,
  onClose,
  open,
}: CopyFromPreviousOpportunityDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { optionState, optionStates, replaceFirstOption, roomAssets } = usePackageOptionsContext();
  const { replaceOptionDrafts } = useInclusionDraftsContext();
  const { replaceDrafts } = useEgsDraftsContext();
  const { replacePeriods } = useSurchargeGridsContext();
  const { stayInputs } = useScrapeSessionContext();
  const [step, setStep] = useState<CopyStep>('opportunity');
  const [sourceOpportunityId, setSourceOpportunityId] = useState('');
  const [selectedSections, setSelectedSections] = useState<Set<CopySection>>(new Set());
  const [pickedSurchargeDates, setPickedSurchargeDates] = useState<Record<string, Partial<SurchargeDates>>>({});
  const [pickedEgsRooms, setPickedEgsRooms] = useState<Record<string, Array<string>>>({});
  const [skippedEgsRows, setSkippedEgsRows] = useState<ReadonlySet<string>>(new Set());

  const { data: offer } = useGetOffer(offerId);
  const {
    data: opportunity,
    error: opportunityError,
    isFetching: isFetchingOpportunity,
  } = useGetOpportunity<App.OpportunityPage>(offerId, { enabled: open });
  const accountId = offer?.vendor_account_id;
  const {
    data: opportunities,
    error: opportunitiesError,
    isLoading: isLoadingOpportunities,
  } = useGetAccountOpportunities(accountId, { enabled: open });

  const choices = useMemo(() => previousOpportunityChoices(opportunities ?? [], offerId), [opportunities, offerId]);
  const source = choices.find((choice) => choice.id === sourceOpportunityId);
  const copiesDealOptions = selectedSections.has('dealOptions');
  const copiesInclusions = selectedSections.has('inclusions');
  const copiesSurcharges = selectedSections.has('surcharges');
  const copiesEgs = selectedSections.has('extraGuestSurcharges');
  const readsRecords = copiesDealOptions || copiesSurcharges || copiesEgs;

  const {
    data: related,
    error: relatedError,
    isFetching: isFetchingRelated,
  } = useGetOpportunityRelated(sourceOpportunityId, { enabled: open && !!sourceOpportunityId && readsRecords });
  const {
    data: targetRelated,
    error: targetRelatedError,
    isFetching: isFetchingTarget,
  } = useGetOpportunityRelated(offerId, {
    enabled: open && readsRecords,
  });

  const netCurrency = targetRelated ? deriveOpportunityCurrency(targetRelated.deals) : undefined;
  const previous = useMemo(
    () =>
      related && netCurrency
        ? reducePreviousDealOptions(related.deals, related.bundled_experiences, netCurrency)
        : undefined,
    [related, netCurrency],
  );
  const previousRoomCount = previous?.rooms.length ?? 0;
  const copiedDealOptions = copiesDealOptions && previousRoomCount > 0 ? previous : undefined;
  // Built once: rooms new to Option 1 get fresh ids, and the copied inclusions must point at these ones.
  const copiedFirstOption = useMemo(
    () =>
      copiedDealOptions
        ? replaceWithPreviousRoomPricing(optionStates[0] ?? DEFAULT_OPTION_STATE, copiedDealOptions)
        : undefined,
    [copiedDealOptions, optionStates],
  );
  const previousInclusions = useMemo(
    () =>
      related && copiedDealOptions && copiedFirstOption
        ? reducePreviousInclusions(
            related.inclusions,
            related.deals,
            copiedDealOptions.copiedDealIds,
            copiedFirstOption,
          )
        : undefined,
    [related, copiedDealOptions, copiedFirstOption],
  );
  const surchargeCurrency = stayInputs?.currencyCode ?? 'AUD';
  const previousSurcharges = useMemo(
    () =>
      related && opportunity
        ? reducePreviousSurcharges(related.surcharge_periods, surchargeCurrency, deriveOpportunityPrefill(opportunity))
        : undefined,
    [related, surchargeCurrency, opportunity],
  );
  const surchargeDatesByKey = useMemo(
    () => chosenSurchargeDates(previousSurcharges?.toDate ?? [], pickedSurchargeDates),
    [previousSurcharges, pickedSurchargeDates],
  );
  const chosenSurcharges = useMemo(
    () => (previousSurcharges ? withChosenSurchargeDates(previousSurcharges, surchargeDatesByKey) : []),
    [previousSurcharges, surchargeDatesByKey],
  );
  const surchargePeriodCount = (previousSurcharges?.periods.length ?? 0) + (previousSurcharges?.toDate.length ?? 0);
  const surchargesToCopy = chosenSurcharges.length;
  const surchargesSkipped = Object.values(surchargeDatesByKey).filter(({ skipped }) => skipped).length;
  const surchargeOverlapWarning = copiesSurcharges
    ? surchargesOverlapWarning(countOverlappingNotAccumulating(chosenSurcharges))
    : undefined;
  const previousEgs = useMemo(
    () =>
      related && netCurrency
        ? reducePreviousEgs(
            related.extra_guest_surcharges,
            netCurrency,
            egsRoomNames(targetRelated, (copiedFirstOption ?? optionState).roomsByCategory, roomAssets),
          )
        : undefined,
    [related, targetRelated, netCurrency, optionState, roomAssets, copiedFirstOption],
  );
  const previousEgsCount = previousEgs?.records.length ?? 0;
  const egsRows = useMemo(() => (previousEgs ? egsRoomRows(previousEgs) : []), [previousEgs]);
  const egsRoomsByNameKey = useMemo(
    () => chosenEgsRooms(egsRows, pickedEgsRooms, previousEgs?.roomNames ?? []),
    [egsRows, pickedEgsRooms, previousEgs],
  );
  const copiedEgs = useMemo(
    () => (previousEgs ? withChosenEgsRooms(previousEgs, egsRoomsByNameKey, skippedEgsRows) : []),
    [previousEgs, egsRoomsByNameKey, skippedEgsRows],
  );
  const previousLos = useMemo(() => (previous ? previousLengthsOfStay(previous) : []), [previous]);
  const sectionDetails = useMemo(() => {
    const details: Partial<Record<CopySection, CopySectionDetails>> = {};
    if (previous && netCurrency) {
      details.dealOptions = previous.rooms.length
        ? { summary: dealOptionsSummary(previous, previousLos), notes: dealOptionsNotes(previous, netCurrency) }
        : { emptyMessage: 'That Opportunity has no Deal Options that can be copied.', notes: [] };
    }
    if (previousInclusions) {
      details.inclusions = previousInclusions.drafts.length
        ? { summary: inclusionsSummary(previousInclusions), notes: inclusionsNotes(previousInclusions) }
        : { emptyMessage: 'That Opportunity has no inclusions on the Deal Options being copied.', notes: [] };
    }
    if (previousSurcharges) {
      details.surcharges = surchargePeriodCount
        ? { summary: surchargesSummary(surchargePeriodCount), notes: surchargesNotes(previousSurcharges) }
        : { emptyMessage: 'That Opportunity has no surcharge periods that can be copied.', notes: [] };
    }
    if (previousEgs && netCurrency) {
      details.extraGuestSurcharges = previousEgs.records.length
        ? { summary: egsSummary(previousEgs.records.length), notes: egsNotes(previousEgs, netCurrency) }
        : { emptyMessage: 'That Opportunity has no extra guest surcharges that can be copied.', notes: [] };
    }
    return details;
  }, [previous, previousLos, previousInclusions, previousSurcharges, surchargePeriodCount, previousEgs, netCurrency]);

  const readiness = {
    hasSource: !!source,
    selected: selectedSections,
    isFetching: isFetchingRelated || isFetchingTarget || (copiesSurcharges && isFetchingOpportunity),
    dealOptionRooms: previousRoomCount,
    inclusions: previousInclusions?.drafts.length ?? 0,
    surchargePeriods: surchargePeriodCount,
    surchargesToDate: previousSurcharges?.toDate.length ?? 0,
    surchargesToCopy,
    everySurchargeDatedOrSkipped: Object.values(surchargeDatesByKey).every(isSurchargeDatesDone),
    egsRecords: previousEgsCount,
    egsToCopy: copiedEgs.length,
    everyEgsHasRoomsOrSkipped: egsRows.every(
      (row) => skippedEgsRows.has(row.key) || egsRoomsByNameKey[row.key].length > 0,
    ),
  };
  const confirmSummaries: Record<CopySection, string | undefined> = {
    dealOptions: sectionDetails.dealOptions?.summary,
    inclusions: sectionDetails.inclusions?.summary,
    surcharges: surchargesSummary(surchargesToCopy, surchargesSkipped),
    extraGuestSurcharges: egsSummary(copiedEgs.length, previousEgsCount - copiedEgs.length),
  };
  const steps = copySteps(readiness);
  const stepIndex = steps.indexOf(step);
  const warning = replaceWarning(selectedSections);

  const handleSurchargeDatesChange = useCallback((key: string, dates: Partial<SurchargeDates>) => {
    setPickedSurchargeDates((current) => ({ ...current, [key]: { ...current[key], ...dates } }));
  }, []);

  const handleEgsRoomsChange = useCallback((key: string, rooms: Array<string>) => {
    setPickedEgsRooms((current) => ({ ...current, [key]: rooms }));
  }, []);

  const handleEgsSkipChange = useCallback((key: string, skipped: boolean) => {
    setSkippedEgsRows((current) => {
      const next = new Set(current);
      if (skipped) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  function handleSourceChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSourceOpportunityId(event.target.value);
    setPickedSurchargeDates({});
    setPickedEgsRooms({});
    setSkippedEgsRows(new Set());
  }

  function handleSectionChange(event: React.ChangeEvent<HTMLInputElement>) {
    const section = COPY_SECTIONS.find(({ key }) => key === event.target.name)?.key;
    const { checked } = event.target;
    if (!section) {
      return;
    }
    setSelectedSections((current) => toggleCopySection(current, section, checked));
  }

  function handleCopy() {
    if (!source) {
      return;
    }
    if (copiedFirstOption) {
      replaceFirstOption(copiedFirstOption);
    }
    if (copiesInclusions && previousInclusions) {
      replaceOptionDrafts(0, previousInclusions.drafts);
    }
    if (copiesSurcharges && previousSurcharges) {
      replacePeriods(chosenSurcharges.map(({ period }) => period));
    }
    if (copiesEgs && previousEgs) {
      replaceDrafts(copiedEgs);
    }
    enqueueSnackbar(`Copied from ${source.name}`, { variant: 'success' });
    onClose();
  }

  function handleExited() {
    setStep('opportunity');
    setSourceOpportunityId('');
    setSelectedSections(new Set());
    setPickedSurchargeDates({});
    setPickedEgsRooms({});
    setSkippedEgsRows(new Set());
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
      slotProps={{ transition: { onExited: handleExited } }}
    >
      <DialogTitle>Copy from previous Opportunity</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          {steps.map((copyStep) => (
            <Step key={copyStep}>
              <StepLabel>{COPY_STEP_LABELS[copyStep]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 'opportunity' && (
          <>
            <Typography color="text.secondary" variant="body2">
              Pick a previous Opportunity for this property. Nothing is changed in Salesforce.
            </Typography>

            {isLoadingOpportunities && <CircularProgress size={24} sx={{ mt: 2 }} />}

            {opportunitiesError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load the previous Opportunities for this property.
              </Alert>
            )}

            {opportunities && choices.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This property has no other Opportunities.
              </Alert>
            )}

            {choices.length > 0 && (
              <RadioGroup onChange={handleSourceChange} sx={{ mt: 2 }} value={sourceOpportunityId}>
                {choices.map((choice) => (
                  <FormControlLabel
                    control={<Radio size="small" />}
                    key={choice.id}
                    label={
                      <Stack>
                        <Typography variant="body2">{choice.name}</Typography>
                        <Typography color="text.secondary" variant="caption">
                          {[choice.stage, choice.closeDate && `Close date ${choice.closeDate}`]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Stack>
                    }
                    value={choice.id}
                  />
                ))}
              </RadioGroup>
            )}
          </>
        )}

        {step === 'sections' && (
          <>
            <Typography color="text.secondary" variant="body2">
              Pick what to copy from {source?.name}.
            </Typography>

            <FormGroup sx={{ mt: 2 }}>
              {COPY_SECTIONS.map((section) => (
                <CopySectionOption
                  checked={selectedSections.has(section.key)}
                  details={isFetchingRelated ? undefined : sectionDetails[section.key]}
                  disabledReason={
                    section.requires && !selectedSections.has(section.requires.section)
                      ? section.requires.reason
                      : undefined
                  }
                  key={section.key}
                  label={section.label}
                  name={section.key}
                  onChange={handleSectionChange}
                />
              ))}
            </FormGroup>

            {readsRecords && isFetchingRelated && <CircularProgress size={24} sx={{ mt: 2 }} />}

            {readsRecords && !isFetchingRelated && relatedError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load that Opportunity from Salesforce.
              </Alert>
            )}

            {copiesSurcharges && !isFetchingOpportunity && !opportunity && opportunityError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load this Opportunity&apos;s travel dates, so surcharge periods can&apos;t be copied.
              </Alert>
            )}

            {readsRecords && !isFetchingTarget && targetRelatedError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load this Opportunity from Salesforce, so nothing can be copied.
              </Alert>
            )}
          </>
        )}

        {step === 'surchargeDates' && previousSurcharges && (
          <CopySurchargeDatesStep
            datesByKey={surchargeDatesByKey}
            nothingToCopy={surchargesToCopy === 0}
            onDatesChange={handleSurchargeDatesChange}
            toDate={previousSurcharges.toDate}
          />
        )}

        {step === 'egsRooms' && previousEgs && (
          <CopyEgsRoomsStep
            nothingToCopy={copiedEgs.length === 0}
            onRoomsChange={handleEgsRoomsChange}
            onSkipChange={handleEgsSkipChange}
            roomNames={previousEgs.roomNames}
            rows={egsRows}
            roomsByNameKey={egsRoomsByNameKey}
            skippedRows={skippedEgsRows}
          />
        )}

        {step === 'confirm' && (
          <>
            <Typography variant="body2">From {source?.name}:</Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {COPY_SECTIONS.filter((section) => selectedSections.has(section.key)).map((section) => (
                <Typography component="li" key={section.key} variant="body2">
                  {`${section.label}: ${confirmSummaries[section.key]}`}
                </Typography>
              ))}
            </Box>

            {surchargeOverlapWarning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {surchargeOverlapWarning}
              </Alert>
            )}

            {warning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {warning}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        {stepIndex > 0 && (
          <Button onClick={() => setStep(steps[stepIndex - 1])} variant="text">
            Back
          </Button>
        )}
        {step === 'confirm' ? (
          <Button disabled={!canCopy(readiness)} onClick={handleCopy} variant="contained">
            Copy
          </Button>
        ) : (
          <Button
            disabled={!isStepComplete(step, readiness)}
            onClick={() => setStep(steps[stepIndex + 1])}
            variant="contained"
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
